import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useCreateMeterReader, useUpdateMeterReader } from "../../hooks";
import { convertKeysToSnakeCase } from "@shared/utils/caseConverter";
import { Button } from "@shared/ui/button";
import { Upload, X } from "lucide-react";
import { toast } from "@shared/ui/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";

interface FileUploadComponentProps {
  file: File | null;
  filePreview: string | null;
  isLoading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
}

export const FileUploadComponent: React.FC<FileUploadComponentProps> =
  React.memo(({ file, filePreview, isLoading, onFileChange, onRemoveFile }) => {
    return (
      <div className="w-full">
        <div
          className={`border rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors min-h-[100px] ${
            filePreview || file
              ? "border-blue-300 bg-blue-50"
              : "border-dashed border-gray-300"
          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          onClick={() =>
            !isLoading && document.getElementById("file-upload")?.click()
          }
        >
          {filePreview || file ? (
            <div className="relative w-full flex flex-col items-center justify-center">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="absolute -top-2 -right-2 rounded-full h-4 w-6 p-0 bg-red-100 hover:bg-red-200"
                disabled={isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFile();
                }}
              >
                <X className="h-3 w-3" />
              </Button>

              {filePreview ? (
                <img
                  src={filePreview}
                  alt="File Preview"
                  className="mx-auto max-h-16 object-contain mb-2"
                />
              ) : (
                <div className="flex flex-col items-center mb-2">
                  <Upload className="h-4 w-8 text-blue-500 mb-1" />
                  <span className="text-sm font-medium text-gray-700">
                    Document
                  </span>
                </div>
              )}

              <p className="text-xs text-center text-gray-600">
                {file?.name && file.name.length > 25
                  ? file.name.substring(0, 25) + "..."
                  : file?.name || "Existing file"}
              </p>
              {file?.size && (
                <p className="text-xs text-gray-500 mt-1">
                  ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          ) : (
            <div className="text-center">
              <Upload className="h-4 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 font-medium">
                Click to upload file
              </p>
            </div>
          )}

          <input
            id="file-upload"
            type="file"
            className="hidden"
            disabled={isLoading}
            onChange={onFileChange}
            accept=""
          />
        </div>
      </div>
    );
  });

interface EditMeterReaderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  details: any;
}

const EditMeterReader: React.FC<EditMeterReaderProps> = ({
  open,
  onOpenChange,
  details,
}) => {

  const remoteUtilityId = useRemoteUtilityId();
  const { mutate: createMeterReader } = useCreateMeterReader();
  const { mutate: updateReader } = useUpdateMeterReader();
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;

      setFile(selectedFile);
      readerForm.setValue("file", selectedFile);
      readerForm.clearErrors("file");

      const reader = new FileReader();
      reader.onload = (evt) => {
        setFilePreview(evt.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    },
    []
  );

  const removeFile = useCallback(() => {
    setFile(null);
    setFilePreview(null);
    readerForm.setValue("file", null);
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  }, []);

  const fields: FormField[] = [
    { name: "employeeId", type: "text", label: "Employee ID", placeholder: 'Enter Employee Id' },
    { name: "email", type: "email", label: "Email", disabled: details?.id, placeholder: 'Enter Email'  },
    { name: "name", type: "text", label: "Name", required: true, placeholder: "Enter Name of Employee" },
    { name: "city", type: "text", label: "City", required: true, placeholder: "Enter city" },
    { name: "address", type: "text", label: "Address", required: true, placeholder: "Enter Address" },
    { name: "version", type: "text", label: "Version", placeholder: "Enter Version" },
    { name: "joiningDate", type: "datepicker", label: "Joining Date", placeholder: "Enter Joining Date" },
    {
      name: "contactNumber",
      type: "text",
      label: "Phone Number",
      required: true,
      placeholder: "Enter Phone Number"
    },
    { name: "password", type: "password", label: "Password", required: true, placeholder: "Enter Password" },
    {
      name: "retypePassword",
      type: "password",
      label: "Retype Password",
      required: true,
      placeholder: "Retype Password"
    },
    { name: "imeiNo", type: "text", label: "IMEI Number", required: true, placeholder: "Enter IMEI Number" },
    {
      name: "file",
      type: "file",
      label: "Upload Reader's Photo",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        file: "w-full",
        error: "text-red-600 text-sm mt-1",
      },
      customRender: () => (
        <FileUploadComponent
          file={file}
          filePreview={filePreview}
          isLoading={isLoading}
          onFileChange={handleFileChange}
          onRemoveFile={removeFile}
        />
      ),
    },
  ];

  const readerForm = FormService.createForm({ fields });

  const formConfig: FormConfig = {
    fields,
    shouldUnregister: true,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4 ",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      radio: "",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };

  const handleSubmitForm = (data: any) => {
    const { password, retypePassword } = data;

    if (password !== retypePassword) {
toast({
  variant: "destructive",
  title: "Error",
  description: "Passwords do not match",
});
      return;
    }

    setIsLoading(true);
    const snakeCasedData = convertKeysToSnakeCase(data);

    const formData = new FormData();
    Object.entries(snakeCasedData).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof File) {
        formData.append(key, value);
      } else if (
        key === "file" &&
        typeof value === "string" &&
        value.startsWith("http")
      ) {
        // Skip
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          formData.append(`${key}[${index}]`, String(item));
        });
      } else if (typeof value === "object") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });

    const onSuccess = () => {
      setIsLoading(false);
      onOpenChange(false);
    };

    const onError = () => setIsLoading(false);

    if (details?.id) {
      updateReader(
        { id: details.id, payload: formData },
        { onSuccess: () => {
          logEvent("MX Meter Reader Detail Updated", {
            ...formData,
            readerId: details?.id
          });
          onSuccess();
        }, onError }
      );
    } else {
      formData.append('remote_utility_id', remoteUtilityId)
      createMeterReader(formData, { onSuccess, onError });
    }
  };

  useEffect(() => {
    if (open) {
      if (details?.id) {
        const updateDetails = { ...details, retypePassword: details?.password };
        FormService.populateForm(readerForm, updateDetails, fields);

        if (details.file) {
          setFilePreview(details.file);
          setFile(details.file);
        }
      } else {
        fields.map((field) => {
          readerForm.setValue(field.name, '');
        })
        setFile(null);
        setFilePreview(null);
      }
    }
  }, [details, open]);

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-6 border-t">
      <button
        type="button"
        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
        onClick={() => onOpenChange(false)}
        disabled={isLoading}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={readerForm.handleSubmit(handleSubmitForm)}
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        disabled={isLoading}
      >
        {isLoading
          ? details?.id
            ? "Updating..."
            : "Creating..."
          : details?.id
          ? "Update"
          : "Create"}
      </button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-[50vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {details?.id ? "Edit Meter Reader" : "Add Meter Reader"}
          </DialogTitle>
        </DialogHeader>
        <DynamicForm
          form={readerForm}
          config={formConfig}
          fields={fields}
          onSubmit={(data) => handleSubmitForm(data)}
          gridColumns={2}
          renderCustomFooter={renderCustomFooter}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditMeterReader;
